import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Zap, Crown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ProcessingMode } from '@/lib/upload/resolveProcessingMode';

interface Props {
  /** The EFFECTIVE mode (post resolveProcessingMode), not the raw selection. */
  value: ProcessingMode;
  onChange: (mode: ProcessingMode) => void;
  /** Free users: the automatic flow is PRO-only (Copiloto is the free mode). */
  automaticLocked: boolean;
}

const cardClass = (selected: boolean, locked: boolean) =>
  `p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 ${
    locked
      ? 'opacity-50 cursor-not-allowed border-border bg-background/50'
      : selected
        ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/5 shadow-glow cursor-pointer hover:scale-105'
        : 'border-border hover:border-primary/40 bg-background/50 cursor-pointer hover:scale-105'
  }`;

/** The Copiloto/Automático processing-mode pair used by every upload/copy
 *  dialog. One component so the PRO gating can never drift between them.
 *  Copiloto is free; Automático carries the PRO badge and is unclickable when
 *  locked. */
export const ProcessingModeSelector: React.FC<Props> = ({ value, onChange, automaticLocked }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-3">
      <div
        data-testid="mode-copilot"
        className={cardClass(value === 'copilot', false)}
        onClick={() => onChange('copilot')}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-xs sm:text-sm text-foreground">{t('copyLibrary.copilot')}</h3>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{t('copyLibrary.copilotDescription')}</p>
      </div>
      <div
        data-testid="mode-automatic"
        className={cardClass(value === 'automatic', automaticLocked)}
        onClick={() => { if (!automaticLocked) onChange('automatic'); }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Zap className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-xs sm:text-sm text-foreground">{t('copyLibrary.automatic')}</h3>
          {automaticLocked && (
            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
              <Crown className="w-3 h-3 mr-1" />
              {t('common.pro')}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{t('copyLibrary.automaticDescription')}</p>
      </div>
    </div>
  );
};

export default ProcessingModeSelector;
